import React, { useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import "./EmployeeDetail.scss";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Empty, Tabs, Tag, Timeline } from "antd";
import { useEmployee } from "Hooks/EmployeeHooks/useEmployees";
import PropTypes from "prop-types";
import {
  ApartmentOutlined,
  IdcardOutlined,
  SolutionOutlined,
  TeamOutlined,
} from "@ant-design/icons";

const CompensationCard = ({ compensation }) => {
  return (
    <Card bordered={false} className="shadow-sm " style={{ borderRadius: "12px" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-bold mb-0">Compensation Details</h5>
        <Tag color={compensation.isActive ? "success" : "default"}>
          {compensation.isActive ? "Active" : "Inactive"}
        </Tag>
      </div>

      <div className="compensation-amount mb-3">
        <span className="amount-currency">{compensation.currency}</span>
        <span className="amount-value">{compensation.baseAmount.toLocaleString()}</span>
        <span className="amount-period">{compensation.payType === 0 ? "/Annual" : "/Monthly"}</span>
      </div>

      <div className="info-grid">
        <div className="info-item">
          <strong style={{ marginRight: "8px" }} className="info-label">
            Compensation ID :
          </strong>
          <span className="info-value">{compensation.publicId}</span>
        </div>
        <div className="info-item">
          <strong style={{ marginRight: "8px" }} className="info-label">
            Pay Type :
          </strong>
          <span className="info-value">{compensation.payType === 0 ? "Annual" : "Monthly"}</span>
        </div>
        <div className="info-item">
          <strong style={{ marginRight: "8px" }} className="info-label">
            Effective Period :
          </strong>
          <span className="info-value">
            {new Date(compensation.effectiveFrom).toLocaleDateString()} →{" "}
            {compensation.effectiveTo
              ? new Date(compensation.effectiveTo).toLocaleDateString()
              : "Present"}
          </span>
        </div>
        <div className="info-item">
          <strong style={{ marginRight: "8px" }} className="info-label">
            Created On :
          </strong>
          <span className="info-value">
            {new Date(compensation.createdOn).toLocaleDateString()}
          </span>
        </div>
      </div>
    </Card>
  );
};

CompensationCard.propTypes = {
  compensation: PropTypes.shape({
    publicId: PropTypes.string.isRequired,
    baseAmount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    payType: PropTypes.number.isRequired,
    effectiveFrom: PropTypes.string.isRequired,
    effectiveTo: PropTypes.string, // This can be null, so it's not required
    isActive: PropTypes.bool.isRequired,
    createdOn: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
  }).isRequired,
};

const EmploymentCard = ({ employment }) => {
  const from = new Date(employment.effectiveFrom);
  const to = employment.effectiveTo ? new Date(employment.effectiveTo) : null;

  const probationStart = new Date(employment.probationStartDate);
  const probationEnd = new Date(employment.probationEndDate);

  const isProbation = from >= probationStart && from <= probationEnd;

  return (
    <Card bordered={false} className="shadow-sm" style={{ borderRadius: "12px" }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="fw-bold mb-0">{employment.jobTitleName}</h5>

        <div>
          {employment.isActive && <Tag color="green">Active</Tag>}

          {isProbation && <Tag color="orange">Probation</Tag>}
        </div>
      </div>

      <div className="mb-2">
        <strong style={{ marginRight: "8px" }}>Department :</strong> {employment.departmentName}
      </div>

      <div className="mb-2">
        <strong style={{ marginRight: "8px" }}>Designation :</strong> {employment.designationName}
      </div>

      <div className="mb-2">
        <strong style={{ marginRight: "8px" }}>Supervisor :</strong> {employment.supervisorName}
      </div>

      <div className="text-muted">
        {from.toLocaleDateString()} → {to ? to.toLocaleDateString() : "Present"}
      </div>
    </Card>
  );
};

EmploymentCard.propTypes = {
  employment: PropTypes.shape({
    employeeId: PropTypes.number.isRequired,
    supervisorId: PropTypes.number,
    departmentId: PropTypes.number,
    jobTitleId: PropTypes.number,
    designationId: PropTypes.number,

    effectiveFrom: PropTypes.string.isRequired,
    effectiveTo: PropTypes.string,

    probationStartDate: PropTypes.string,
    probationEndDate: PropTypes.string,
    actualProbationEndDate: PropTypes.string,

    isActive: PropTypes.bool.isRequired,

    supervisorName: PropTypes.string,
    departmentName: PropTypes.string,
    jobTitleName: PropTypes.string,
    designationName: PropTypes.string,
  }).isRequired,
};

function EmployeeDetail() {
  const { empId } = useParams();
  const navigate = useNavigate();

  const {
    fetchEmployeeDetail,
    employeeDetail,
    loadingDetail,

    compensations,
    loadingCompensation,
    fetchCompensations,
    compensationsLoaded,

    employments,
    loadingEemployments,
    employmentsLoaded,
    fetchEmployments,

 
    columns,

    resetEmployeeTabs
  } = useEmployee();

  useEffect(() => {
    if (empId) {
      resetEmployeeTabs();
      fetchEmployeeDetail(empId);}
  }, [empId]);

  // StatCard component
  const StatCard = ({ title, value, publicId, icon: Icon, color }) => {
      const colorMap = {
    // Change these to match your green theme
    primary: "#599c16", // var(--primary-main)
    success: "#206d1a", // var(--primary-focus)
    info: "#7bad67",    // var(--secondary-main)
    warning: "#f59e0b", 
    danger: "#ef4444",
  };
    const activeColor = colorMap[color] || colorMap.primary;

    return (
      <div className="col-xl-3 col-md-6 mb-3">
        <Card
          bordered={false}
          className="shadow-sm"
          style={{ borderRadius: "10px", minHeight: "90px" }}
          bodyStyle={{ padding: "12px 14px" }}

        >
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <span className="text-muted small">{title}</span>
              <div className="stat-value">{value}</div>
              {publicId && <small className="text-muted">{publicId}</small>}
            </div>
            {Icon && <Icon style={{ fontSize: "1.6rem", color: activeColor }} />}
          </div>
        </Card>
      </div>
    );
  };

  StatCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    publicId: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    color: PropTypes.string,
  };

  const handleTabChange = (key) => {
    if (key === "2" && !compensationsLoaded) {
      fetchCompensations(empId);
    }

    if (key === "3" && !employmentsLoaded) {
      fetchEmployments(empId);
    }

    
  };

  if (loadingDetail || !employeeDetail) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div style={{ padding: 24 }}>Loading Employee Detail...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div style={{ padding: "24px" }}>
        {/*  Header */}
        <div
          className="employee-header"
          style={{
            background: "linear-gradient(to right, #f8f9fa, #ffffff)",
            padding: "24px 32px",
            borderRadius: "12px",
            marginBottom: "24px",
            border: "1px solid #e9ecef",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <div className="d-flex align-items-center mb-2">
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "50%",
                    backgroundColor: "#e9ecef",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: "16px",
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#495057",
                  }}
                >
                  {employeeDetail.fullName ? employeeDetail.fullName.charAt(0).toUpperCase() : "E"}
                </div>
                <div>
                  <h1
                    className="mb-1"
                    style={{
                      fontSize: "24px",
                      fontWeight: "600",
                      color: "#212529",
                      margin: 0,
                    }}
                  >
                    {employeeDetail.fullName || "Employee Name"}
                  </h1>
                  <div className="d-flex align-items-center mt-1">
                    <Tag color="green" style={{ fontSize: "12px", padding: "2px 8px" }}>
                      {employeeDetail.designationName || "Designation"}
                    </Tag>
                    <span className="text-muted ms-2" style={{ fontSize: "14px" }}>
                      ID: #{employeeDetail.publicId || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button
                type="default"
                icon={<i className="fas fa-arrow-left"></i>}
                onClick={() => navigate(-1)}
                style={{
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Back
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Row */}
     <div className="row g-3 mb-4">
  <StatCard
    title="Current Department"
    value={employeeDetail?.department?.name || "Org-Wide Role"}
    publicId={employeeDetail?.department?.publicId || ""}
    icon={ApartmentOutlined}
    color="primary"
  />

  <StatCard
    title="Current Job Title"
    value={employeeDetail?.jobTitle?.name || "N/A"}
    publicId={employeeDetail?.jobTitle?.publicId || ""}
    icon={IdcardOutlined}
    color="info"
  />

  <StatCard
    title="Current Designation"
    value={employeeDetail?.designation?.name || "N/A"}
    publicId={employeeDetail?.designation?.publicId || ""}
    icon={SolutionOutlined}
    color="warning"
  />

  <StatCard
    title="Current Supervisor"
    value={employeeDetail?.supervisor?.fullName || "N/A"}
    publicId={employeeDetail?.supervisor?.publicId || ""}
    icon={TeamOutlined}
    color="success"
  />
</div>

        {/* Tabs Area */}
        <Tabs defaultActiveKey="1" onChange={handleTabChange} className="custom-tabs">
          <Tabs.TabPane
            tab={
              <span>
                <ApartmentOutlined />
                Overview
              </span>
            }
            key="1"
          >
            <div className="employee-overview">
              <div className="row g-4">
                {/* Personal Information */}
                <div className="col-lg-6">
                  <Card
                    title={
                      <>
                        <i className="fas fa-user me-2"></i>Personal Information
                      </>
                    }
                    className="card-professional"
                    headStyle={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}
                  >
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Full Name :
                      </span>
                      <span className="info-value ">{employeeDetail.fullName || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Email :
                      </span>
                      <span className="info-value">{employeeDetail.workEmail || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Phone :
                      </span>
                      <span className="info-value">{employeeDetail.mobileno || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Gender :
                      </span>
                      <span className="info-value">{employeeDetail.gender || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Marital Status :
                      </span>
                      <span className="info-value">{employeeDetail.maritalStatus || "N/A"}</span>
                    </div>
                  </Card>
                </div>

                {/* Employment Information */}
                <div className="col-lg-6">
                  <Card
                    title={
                      <>
                        <i className="fas fa-briefcase me-2"></i>Employment Information
                      </>
                    }
                    className="card-professional"
                    headStyle={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}
                  >
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Employee ID :
                      </span>
                      <span className="info-value">{employeeDetail.publicId || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Department :
                      </span>
                      <span className="info-value">{employeeDetail.department?.name || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Job Title :
                      </span>
                      <span className="info-value">{employeeDetail.jobTitle?.name || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Designation :
                      </span>
                      <span className="info-value">
                        {employeeDetail.designation?.name || "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Hire Date :
                      </span>
                      <span className="info-value">
                        {employeeDetail.hiredDate
                          ? new Date(employeeDetail.hiredDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                  </Card>
                </div>

                {/* Address Information */}
                <div className="col-lg-6">
                  <Card
                    title={
                      <>
                        <i className="fas fa-map-marker-alt me-2"></i>Address Information
                      </>
                    }
                    className="card-professional"
                    headStyle={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}
                  >
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Street Address :
                      </span>
                      <span className="info-value">{employeeDetail.streetAddress || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        City :
                      </span>
                      <span className="info-value">{employeeDetail.city || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        State :
                      </span>
                      <span className="info-value">{employeeDetail.state || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Postal Code :
                      </span>
                      <span className="info-value">{employeeDetail.postalCode || "N/A"}</span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Country :
                      </span>
                      <span className="info-value">{employeeDetail.country || "N/A"}</span>
                    </div>
                  </Card>
                </div>

                {/* Supervisor & Probation Information */}
                <div className="col-lg-6">
                  <Card
                    title={
                      <>
                        <i className="fas fa-user-tie me-2"></i>Supervisor & Probation
                      </>
                    }
                    className="card-professional"
                    headStyle={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}
                  >
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Supervisor :
                      </span>
                      <span className="info-value">
                        {employeeDetail.supervisor?.fullName || "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Supervisor Email :
                      </span>
                      <span className="info-value">
                        {employeeDetail.supervisor?.workEmail || "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Probation Start :
                      </span>
                      <span className="info-value">
                        {employeeDetail.probationStartDate
                          ? new Date(employeeDetail.probationStartDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Probation End :
                      </span>
                      <span className="info-value">
                        {employeeDetail.probationEndDate
                          ? new Date(employeeDetail.probationEndDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                    <div className="info-item">
                      <span style={{ marginRight: "8px" }} className="info-label">
                        Actual Probation End :
                      </span>
                      <span className="info-value">
                        {employeeDetail.actualProbationEndDate
                          ? new Date(employeeDetail.actualProbationEndDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </Tabs.TabPane>

          {/* Compensation Tab */}
          <Tabs.TabPane
            tab={
              <span>
                <SolutionOutlined /> Compensation
              </span>
            }
            key="2"
          >
            <div className="timeline-wrapper">
              {loadingCompensation ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status"></div>
                  <p className="mt-2">Loading compensation details...</p>
                </div>
              ) : compensations && compensations.length > 0 ? (
                <Timeline mode="left" className="custom-timeline">
                  {compensations.map((compensation) => {
                    const fromDate = new Date(compensation.effectiveFrom);
                    const toDate = compensation.effectiveTo
                      ? new Date(compensation.effectiveTo)
                      : null;

                    const year = fromDate.getFullYear();

                    return (
                      <Timeline.Item
                        key={compensation.publicId}
                        label={<div className="timeline-year">{year}</div>}
                      >
                        <CompensationCard compensation={compensation} />
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              ) : (
                <Empty
                  description="No compensation records found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </Tabs.TabPane>

          {/* Employment Tab*/}
          <Tabs.TabPane
            tab={
              <span>
                <TeamOutlined /> Employment Timeline
              </span>
            }
            key="3"
          >
            <div className="timeline-wrapper">
              {loadingEemployments ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary"></div>
                  <p>Loading employment history...</p>
                </div>
              ) : employments && employments.length > 0 ? (
                <Timeline mode="left" className="custom-timeline">
                  {employments.map((employment, index) => {
                    const fromDate = new Date(employment.effectiveFrom);
                    const year = fromDate.getFullYear();

                    return (
                      <Timeline.Item
                        key={index}
                        label={<div className="timeline-year">{year}</div>}
                      >
                        <EmploymentCard employment={employment} />
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              ) : (
                <Empty
                  description="No employment history found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </Tabs.TabPane>

        </Tabs>
      </div>
    </DashboardLayout>
  );
}

export default EmployeeDetail;
